import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import classNames from "classnames";
import { PropTypes } from "prop-types";
import React from "react";
import autoBind from "react-autobind";
import TaskActions from "~/actions/task_actions";

import Tooltipable from "~/components/effects/tooltipable";
import DateTimePicker from "~/components/forms/DateTimePicker";
import AsyncSelect from "~/components/forms/dropdowns/SelectWrapper/AsyncSelect";
import Select from "~/components/forms/dropdowns/SelectWrapper/Select";
import { TeammatePicker } from "~/components/forms/dropdowns/TeamMemberSelect";
import TaskEditor from "~/components/forms/HtmlEditors/TaskEditor";
import TaskTemplateSelect from "~/components/forms/Templates/TaskTemplateSelect";
import VariableMenuButton from "~/components/forms/VariableMenuButton";
import InsertDocumentButton from "~/components/insert_document_button";
import UnsupportedVariablesWarning from "~/components/modals/UnsupportedVariablesWarning";
import { TASK_CATEGORY_OPTIONS } from "~/constants/dropdowns/TaskCategoryOptions";
import { TASK_PRIORITY_OPTIONS } from "~/constants/dropdowns/TaskPriorityOptions";
import { TASK_STATUS_OPTIONS } from "~/constants/dropdowns/TaskStatusOptions";
import { isAdminOrStaff } from "~/helpers/userRoleHelper";
import dayjs from "~/lib/dayjs";
import { normalizeUnsupportedVariablesError } from "~/lib/unsupportedVariables";
import TaskStore from "~/stores/task_store";
import AppModal from "./app_modal";

const formatError = (error) =>
  Array.isArray(error) ? error.join(", ") : error;

class TaskModal extends React.Component {
  constructor(props) {
    super(props);

    const { taskID } = props;

    this._isMounted = false;

    this.state = {
      loadingTask: !!taskID,
      task: {},
      errors: {},
      submitting: false,
      submissionIntent: "update",
      templateDescription: null,
      linksToInsert: [],
      variablesWarning: null,
    };

    autoBind(this);
    this.taskNameRef = React.createRef();
  }

  componentDidMount() {
    this._isMounted = true;

    // listen to taskStoreListener changes
    this.taskStoreListener = TaskStore.addListener(this.onTaskStoreChange);

    const { taskID } = this.props;

    if (taskID && this._isMounted) {
      TaskActions.loadTask(taskID, this.onTaskLoad);
    }
  }

  componentWillUnmount() {
    this._isMounted = false;

    // remove listener to TaskStore changes on Unmount
    if (this.taskStoreListener) {
      this.taskStoreListener.remove();
    }
  }

  handleSubmit(e, saveAnyway = false) {
    e.preventDefault();

    const { task } = this.state;
    const { lead, loadLead } = this.props;

    this.setState((previousState) => ({
      submitting: true,
      submissionIntent: saveAnyway ? previousState.submissionIntent : "update",
    }));

    const errors = this.validate();

    if (_lodash.size(errors) === 0) {
      const options = {
        saveAnyway,
        onUnsupportedVariables: this.handleUnsupportedVariables,
        onDone: () => this.setState({ variablesWarning: null }),
        onFail: () => {
          this.setState({ submitting: false, variablesWarning: null });
          GlobalContainer.notify(
            "Failed to save task. Please try again.",
            "error",
          );
        },
      };

      if (loadLead) {
        TaskActions.updateTask(
          {
            ...task,
            id: task.id,
            leadID: lead.id,
          },
          undefined,
          options,
        );
      } else {
        TaskActions.updateTask({ ...task, id: task.id }, undefined, options);
      }
    } else {
      this.setState({ submitting: false, errors });
    }
  }

  onTaskLoad(task) {
    this.setState({
      loadingTask: false,
      task,
    });
  }

  handleUnsupportedVariables(warning) {
    this.setState({
      submitting: false,
      variablesWarning: normalizeUnsupportedVariablesError(warning),
    });
  }

  onTaskStoreChange() {
    const taskState = TaskStore.getState();
    const { task, errors, lastTaskStoreAction } = taskState;
    const { submissionIntent } = this.state;
    const $modal = $(this.appModal.modal);

    let nextState = { taskState };

    if (lastTaskStoreAction === "updateTaskDone") {
      $modal.modal("hide");
      const message =
        submissionIntent === "mark_done"
          ? `Task "${task.name}" marked as done.`
          : `Task "${task.name}" updated.`;
      GlobalContainer.notify(message, "success");
    }

    if (lastTaskStoreAction === "updateTaskFail") {
      if (errors) {
        nextState = { ...nextState, errors, submitting: false };
      }
    }

    this.setState(nextState);
  }

  setTaskField(name, val) {
    this.setState((prevState) => ({
      task: {
        ...prevState.task,
        [name]: val,
      },
    }));
  }

  applyTemplate(template) {
    // Edit modal: clearing selection intentionally does not reset existing task data
    if (!template) {
      return;
    }

    this.setState((prevState) => {
      const taskUpdates = {
        name: template.name || prevState.task.name,
        category: template.category || prevState.task.category,
        description: template.description || prevState.task.description,
        priority: template.priority || prevState.task.priority,
      };
      if (template.owner_id) {
        taskUpdates.owner_id = template.owner_id;
      }
      if (template.status) {
        taskUpdates.status = template.status;
      }

      return {
        task: { ...prevState.task, ...taskUpdates },
        templateDescription: template.description || null,
        errors: {},
      };
    });
  }

  handleMarkAsDoneTaskClick = (e) => {
    e.preventDefault();

    this.setTaskField("status", "done");

    this.setState({ submitting: true, submissionIntent: "mark_done" }, () => {
      const { task } = this.state;
      const { lead, loadLead } = this.props;

      const errors = this.validate();

      if (_lodash.size(errors) === 0) {
        const options = {
          onUnsupportedVariables: this.handleUnsupportedVariables,
          onDone: () => this.setState({ variablesWarning: null }),
          onFail: () => {
            this.setState({ submitting: false, variablesWarning: null });
            GlobalContainer.notify(
              "Failed to save task. Please try again.",
              "error",
            );
          },
        };

        if (loadLead) {
          TaskActions.updateTask(
            {
              ...task,
              id: task.id,
              leadID: lead.id,
            },
            undefined,
            options,
          );
        } else {
          TaskActions.updateTask({ ...task, id: task.id }, undefined, options);
        }
      } else {
        this.setState({ submitting: false, errors });
      }
    });
  };

  validate() {
    const { task } = this.state;
    const errors = {};

    if (!task.name) {
      errors.name = "Can't be empty";
    }

    if (!task.category) {
      errors.category = "Can't be empty";
    }

    if (!task.due_date_at) {
      errors.due_date_at = "Can't be empty";
    }

    if (!task.priority) {
      errors.priority = "Can't be empty";
    }

    if (!task.status) {
      errors.status = "Can't be empty";
    }

    if (_lodash.isEmpty(task.lead_ids)) {
      errors.lead_ids = "Can't be empty";
    }

    this.setState({ errors });

    return errors;
  }

  renderTaskForm() {
    const { loadingTask, task, errors } = this.state;

    if (loadingTask) {
      return (
        <div className="text-center">
          <FontAwesomeIcon icon="far fa-spinner" pulse size="lg" />
        </div>
      );
    }

    const descriptionError = errors.description || errors.base;
    const variablesEnabled = isAdminOrStaff();

    return (
      <>
        <div className="form-row">
          <div className="form-group col-md-4">
            <label htmlFor="task_name">Task Name</label>
            <div className="input-group">
              <input
                ref={this.taskNameRef}
                type="text"
                value={task.name}
                id="task_name"
                name="name"
                className={classNames(
                  "form-control",
                  "d-block",
                  errors.name ? "has-error" : "",
                )}
                placeholder="Enter Name"
                onChange={(val) =>
                  this.setTaskField("name", val && val.target.value)
                }
              />
              {variablesEnabled && (
                <div className="input-group-append">
                  <VariableMenuButton
                    inputRef={this.taskNameRef}
                    value={task.name}
                    onChange={(value) => this.setTaskField("name", value)}
                    includeAlertVariables
                  />
                </div>
              )}
            </div>
          </div>
          <div className="form-group col-md-4">
            <label htmlFor="task-category">Task Category</label>
            <Select
              id="task-category"
              name="category"
              placeholder="Select Category"
              options={TASK_CATEGORY_OPTIONS}
              value={task.category}
              isInvalid={!!errors.category}
              onChange={(val) =>
                this.setTaskField("category", val?.value || "")
              }
              isSearchable
            />
          </div>
          <div className="form-group col-md-4">
            <label htmlFor="task_due_date_at">Due Date</label>
            <DateTimePicker
              id="task_due_date_at"
              placeholder="Select Due Date"
              pickerType="date"
              minDate={dayjs().startOf("day").toDate()}
              value={
                task.due_date_at ? dayjs(task.due_date_at).format("LL") : null
              }
              onChange={(picker) =>
                this.setTaskField(
                  "due_date_at",
                  picker && picker.format("YYYY-MM-DD"),
                )
              }
            />
          </div>
        </div>

        <div className="form-group mb15">
          <label htmlFor="task-description" className="d-block">
            Description
          </label>
          <TaskEditor
            className={descriptionError ? "tiny-mce has-error" : ""}
            onChange={(html) => this.setTaskField("description", html)}
            placeholder="Enter Description"
            value={task.description}
            leadId={this.props.lead?.id}
            enableVariables={variablesEnabled}
            includeOwnerVariables
            includeAlertVariables
            linksToInsert={this.state.linksToInsert}
            clearLinksToInsert={() => this.setState({ linksToInsert: [] })}
            replaceValue={this.state.templateDescription}
            clearReplaceValue={() =>
              this.setState({ templateDescription: null })
            }
          />
          {descriptionError && (
            <div className="invalid-feedback d-block">
              {formatError(descriptionError)}
            </div>
          )}
        </div>

        <div className="form-row">
          <div className="form-group col-lg-8">
            <label htmlFor="related-contacts-ids" className="d-block">
              Related Contacts
            </label>
            <AsyncSelect
              id="related-contacts-ids"
              placeholder="Please Select Contacts ..."
              resourcePath="leads"
              formatOption={(lead) => ({
                value: lead.id,
                label: lead.name,
              })}
              isMulti
              isSearchable
              isInvalid={!!errors.lead_ids}
              value={task.lead_ids}
              onChange={(values) =>
                this.setTaskField(
                  "lead_ids",
                  values ? values.map((item) => item.value) : [],
                )
              }
            />
          </div>
          <div className="form-group col-lg-4">
            <TaskTemplateSelect
              applyTemplate={this.applyTemplate}
              name={task.name}
              category={task.category}
              description={task.description}
              priority={task.priority}
              owner_id={task.owner_id}
              status={task.status}
            />
          </div>
        </div>

        <div className="form-row">
          <div className="form-group col-md-4">
            <label htmlFor="task_owner_id" className="form-control-label">
              Owner
            </label>
            <TeammatePicker
              id="task_owner_id"
              name="owner_id"
              placeholder="Owner"
              value={task.owner_id}
              onChange={(opt) =>
                this.setTaskField("owner_id", opt && opt.value)
              }
              isClearable
            />
          </div>

          <div className="form-group col-md-4">
            <label htmlFor="task-priority">Priority</label>
            <Select
              id="task-priority"
              name="priority"
              placeholder="Select Priority"
              value={task.priority}
              options={TASK_PRIORITY_OPTIONS}
              isSearchable
              isInvalid={!!errors.priority}
              onChange={(val) =>
                this.setTaskField("priority", val?.value || "")
              }
            />
          </div>

          <div className="form-group col-md-4">
            <label htmlFor="task-status">Status</label>
            <Select
              id="task-status"
              name="status"
              placeholder="Select Status"
              value={task.status}
              options={TASK_STATUS_OPTIONS}
              isSearchable
              isInvalid={!!errors.status}
              onChange={(val) => this.setTaskField("status", val?.value || "")}
            />
          </div>
        </div>
      </>
    );
  }

  render() {
    const { task, submitting, variablesWarning } = this.state;
    const { containerID, modalClass, dialogClass } = this.props;

    return (
      <>
        <AppModal
          containerID={containerID}
          modalClass={modalClass}
          dialogClass={dialogClass}
          ref={(appModal) => (this.appModal = appModal)}
        >
          <form
            method="PUT"
            action={`/tasks/${task.id}`}
            onSubmit={this.handleSubmit}
          >
            <div className="modal-header">
              <h5 className="modal-title" id="appModalTask">
                Edit Task
              </h5>

              <button
                type="button"
                className="close"
                data-dismiss="modal"
                aria-label="Close"
              >
                <span aria-hidden="true">&times;</span>
              </button>
            </div>

            <div className="modal-body">{this.renderTaskForm()}</div>

            <div className="modal-footer d-flex align-items-center">
              <InsertDocumentButton
                setLinksToInsert={(links) =>
                  this.setState({ linksToInsert: links })
                }
              />
              <div className="ml-auto">
                <button
                  type="button"
                  className="btn btn-secondary mr-2"
                  data-dismiss="modal"
                >
                  Cancel
                </button>
                {submitting ? (
                  <button
                    type="submit"
                    className="btn btn-primary disabled"
                    disabled
                  >
                    <FontAwesomeIcon
                      icon="far fa-spinner"
                      pulse
                      className="mr5"
                    />{" "}
                    Updating ...
                  </button>
                ) : (
                  <>
                    <button type="submit" className="btn btn-primary">
                      Update
                    </button>
                    {task.status !== "done" && (
                      <Tooltipable text="Mark Task as Done">
                        <button
                          type="button"
                          className="btn btn-primary ml-2"
                          aria-label="Mark Task as Done"
                          onClick={this.handleMarkAsDoneTaskClick}
                        >
                          <FontAwesomeIcon icon={["far", "fa-check-square"]} />
                        </button>
                      </Tooltipable>
                    )}
                  </>
                )}
              </div>
            </div>
          </form>
        </AppModal>
        <UnsupportedVariablesWarning
          show={!!variablesWarning}
          warning={variablesWarning}
          saving={submitting}
          onReview={() => this.setState({ variablesWarning: null })}
          onSaveAnyway={() =>
            this.setState({ variablesWarning: null }, () =>
              this.handleSubmit({ preventDefault: () => false }, true),
            )
          }
        />
      </>
    );
  }
}

TaskModal.defaultProps = {
  lead: {},
  loadLead: true,
};

TaskModal.propTypes = {
  taskID: PropTypes.number.isRequired,
  lead: PropTypes.shape({}),
  loadLead: PropTypes.bool,
};

export default TaskModal;
